import { motion } from "framer-motion";
import { fmtM } from "../../lib/model";

const HATCH = "repeating-linear-gradient(135deg, #9FB0BC 0 2px, #DCE3E8 2px 6px)";

// steps: [{label, value, kind: 'total'|'add'|'sub'}]
export const Waterfall = ({ steps, testid, height = 300, decimals = 3 }) => {
  let run = 0;
  const bars = steps.map((s) => {
    const start = s.kind === "total" ? 0 : run;
    run = s.kind === "total" ? s.value : s.kind === "add" ? run + s.value : run - s.value;
    return { ...s, lo: Math.min(start, run), hi: Math.max(start, run), end: run };
  });
  const max = Math.max(...bars.map((b) => b.hi), 1);
  const plot = height - 44;
  const y = (v) => (v / max) * plot;
  return (
    <div data-testid={testid} className="w-full">
      <div className="flex items-end gap-3 sm:gap-5 border-b border-line" style={{ height }}>
        {bars.map((b, i) => {
          const h = Math.max(y(b.hi - b.lo), 4);
          const bottom = y(b.lo);
          const final = i === bars.length - 1;
          const style = final ? "bg-arq" : b.kind === "total" ? "bg-ink" : b.kind === "add" ? "bg-arq-mid" : "";
          const next = bars[i + 1];
          return (
            <div key={b.label} className="relative flex-1 h-full" data-testid={`${testid}-bar-${i}`}>
              <motion.div
                className={`absolute left-0 right-0 rounded-[3px] ${style}`}
                style={{ bottom, background: b.kind === "sub" ? HATCH : undefined }}
                initial={{ height: 0 }}
                animate={{ height: h }}
                transition={{ duration: 0.7, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] }}
              />
              {next && next.kind !== "total" && (
                <div className="absolute left-full border-t border-dashed border-steel/60 w-3 sm:w-5" style={{ bottom: y(b.end) }} />
              )}
              <div
                className={`absolute left-1/2 -translate-x-1/2 whitespace-nowrap text-center font-mono text-[8.5px] sm:text-xs tabular-nums ${final ? "text-arq font-semibold" : b.kind === "sub" ? "text-[#5C6873]" : "text-char"}`}
                style={{ bottom: bottom + h + 8 }}
              >
                {b.kind === "sub" ? "−" : b.kind === "add" ? "+" : ""}
                {fmtM(b.value, decimals)}
              </div>
            </div>
          );
        })}
      </div>
      <div className="flex gap-3 sm:gap-5 mt-3">
        {bars.map((b, i) => (
          <div key={b.label} className={`flex-1 text-[11px] sm:text-xs leading-snug ${i === bars.length - 1 ? "text-arq font-semibold" : "text-steel"}`}>
            {b.label}
          </div>
        ))}
      </div>
      <div className="mt-6 flex flex-wrap gap-5 text-[11px] text-steel">
        <span className="flex items-center gap-2"><span className="h-2.5 w-4 rounded-sm bg-ink" />Subtotal</span>
        <span className="flex items-center gap-2"><span className="h-2.5 w-4 rounded-sm bg-arq-mid" />Addition</span>
        <span className="flex items-center gap-2"><span className="h-2.5 w-4 rounded-sm" style={{ background: HATCH }} />Deduction</span>
        <span className="flex items-center gap-2"><span className="h-2.5 w-4 rounded-sm bg-arq" />Result</span>
      </div>
    </div>
  );
};
